import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { User, Session, AuthError } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

export interface NotificationPreferences {
  task_reminder?: boolean;
  invoice_paid?: boolean;
  maintenance_alert?: boolean;
  weekly_summary?: boolean;
  /** Email when cleaner reports an incident during entry walkthrough (default true if unset). */
  cleaner_incident_email?: boolean;
  /** Email when post-clean inventory is submitted with low-stock items (default true if unset). */
  inventory_low_email?: boolean;
  /** Email when cleaner reports damage or issues during a clean (default true if unset). */
  maintenance_issues_email?: boolean;
  /** Email when cleaner marks a turnover cleaning complete (default true if unset). */
  cleaning_complete_email?: boolean;
}

interface Profile {
  id: string;
  role: "owner" | "cleaner" | "company_admin" | "pending_invite";
  full_name: string | null;
  first_name: string | null;
  last_name: string | null;
  company_id: string | null;
  team_name: string | null;
  created_at: string;
  email_verified: boolean;
  language?: string;
  avatar_url?: string | null;
  phone?: string | null;
  timezone?: string | null;
  notification_preferences?: NotificationPreferences | null;
}

interface SignUpOptions {
  email: string;
  password: string;
  role: "owner" | "cleaner" | "company_admin" | "pending_invite";
  fullName?: string;
  firstName?: string;
  lastName?: string;
  companyName?: string;
  companyId?: string; // For cleaner invite links (legacy)
  /** Optional team name (e.g. cleaning company); stored in profile and used when accepting company_admin invite. */
  teamName?: string;
  language?: string; // Preferred language for cleaners
  /** After email confirmation, user is sent here. Use /invite/complete when signing up from invite. */
  emailRedirectTo?: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signUp: (options: SignUpOptions) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
  refetchProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  const createProfileIfMissing = async (_userId: string, _userEmail: string) => {
    try {
      // RPC uses auth.uid() server-side; creates profile if missing, returns existing or new profile
      const { data, error } = await supabase.rpc("create_profile_if_missing");

      if (error) {
        console.error("Error creating profile via RPC:", error);
        return null;
      }
      // RPC returns profile as jsonb (single object)
      return data as Profile | null;
    } catch (err) {
      console.error("Exception creating profile:", err);
      return null;
    }
  };

  const fetchProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", userId)
        .maybeSingle();

      if (error) {
        console.error("Error fetching profile:", error);
        return null;
      }
      
      if (!data) {
        console.warn("No profile found for user:", userId);
        return null;
      }
      
      return data as Profile | null;
    } catch (err) {
      console.error("Exception fetching profile:", err);
      return null;
    }
  };

  const fetchOrCreateProfile = async (userId: string, userEmail: string) => {
    let profile = await fetchProfile(userId);
    if (profile) return profile;

    // Create via RPC (works even if DB trigger didn't run, e.g. legacy users)
    const createdProfile = await createProfileIfMissing(userId, userEmail);
    if (createdProfile) {
      // RPC returns jsonb (snake_case); normalize to match Profile if needed
      return (typeof createdProfile === "object" && createdProfile !== null)
        ? (createdProfile as unknown as Profile)
        : await fetchProfile(userId);
    }

    // Retry fetch (trigger or RPC may have just committed)
    await new Promise((r) => setTimeout(r, 300));
    profile = await fetchProfile(userId);
    if (profile) return profile;

    await new Promise((r) => setTimeout(r, 600));
    profile = await fetchProfile(userId);
    return profile ?? null;
  };

  // Slice 56: with no signal the app still opens: use the profile saved on this phone last time.
  const profileCacheKey = (userId: string) => `stravo:profileCache:${userId}`;
  const fetchOrCreateProfileWithCache = async (userId: string, userEmail: string) => {
    const fresh = await fetchOrCreateProfile(userId, userEmail);
    try {
      if (fresh) {
        localStorage.setItem(profileCacheKey(userId), JSON.stringify(fresh));
        return fresh;
      }
      const cached = localStorage.getItem(profileCacheKey(userId));
      return cached ? (JSON.parse(cached) as Profile) : null;
    } catch {
      return fresh;
    }
  };

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);

        // Only fetch profile on sign-in or initial session; skip TOKEN_REFRESHED to avoid
        // repeated fetches and re-renders that can cause navigation throttling / white screen
        if (session?.user) {
          if (event === "INITIAL_SESSION" || event === "SIGNED_IN") {
            fetchOrCreateProfileWithCache(session.user.id, session.user.email || '')
              .then((p) => setProfile(p))
              .catch(() => setProfile(null));
          }
        } else {
          setProfile(null);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        fetchOrCreateProfileWithCache(session.user.id, session.user.email || '')
          .then((p) => setProfile(p))
          .catch(() => setProfile(null))
          .finally(() => setLoading(false));
      } else {
        setLoading(false);
      }
    });

    // Safeguard: never leave loading true forever (e.g. profile fetch hangs for team members)
    const timeout = setTimeout(() => setLoading(false), 10000);
    return () => {
      clearTimeout(timeout);
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) {
        console.error("[Auth] signIn error", { status: error.status, message: error.message });
        return { error };
      }
      
      if (data?.user) {
        let p = await fetchOrCreateProfile(data.user.id, data.user.email || '');
        if (!p) {
          await new Promise((r) => setTimeout(r, 500));
          p = await createProfileIfMissing(data.user.id, data.user.email || '') as Profile | null;
        }
        if (!p) {
          await new Promise((r) => setTimeout(r, 800));
          p = await fetchProfile(data.user.id);
        }
        if (p) setProfile(p);
      }
      
      return { error: null };
    } catch (err) {
      console.error("Sign in exception:", err);
      // Create a proper AuthError-like object
      const authError: AuthError = {
        message: err instanceof Error ? err.message : "An unexpected error occurred",
        name: "AuthError",
        status: 500,
      } as AuthError;
      return { error: authError };
    }
  };

  const signUp = async ({ email, password, role, fullName, firstName, lastName, companyName, companyId, teamName, language, emailRedirectTo }: SignUpOptions) => {
    const redirectUrl = emailRedirectTo ?? `${window.location.origin}/`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          role,
          full_name: fullName || null,
          first_name: firstName || null,
          last_name: lastName || null,
          company_name: companyName || null,
          company_id: companyId || null,
          team_name: teamName ? teamName.trim() || null : null,
          language: language || "en",
        },
      },
    });
    if (error) {
      console.error("[Auth] signUp error", { status: error.status, message: error.message });
    }
    return { error };
  };

  const signOut = async () => {
    // Local only: signing out on one phone/laptop must not revoke other devices.
    // Global sign-out was sending other sessions to /auth/callback with a dead refresh token.
    try {
      await supabase.auth.signOut({ scope: "local" });
    } catch (err) {
      console.error("[Auth] signOut error", err);
    }
    setUser(null);
    setSession(null);
    setProfile(null);
  };

  const refetchProfile = async () => {
    if (user) {
      const updatedProfile = await fetchOrCreateProfile(user.id, user.email || '');
      setProfile(updatedProfile);
    }
  };

  return (
    <AuthContext.Provider value={{ user, session, profile, loading, signIn, signUp, signOut, refetchProfile }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
